'use client';

import AppLogo from '@/components/ui/AppLogo';
import { useCart } from '@/contexts/CartContext';
import { FEATURE_STATS_ENABLED, FEATURE_TESTIMONIOS_ENABLED } from '@/lib/features';
import type { GiftCardVariant } from '@/lib/shopify/gift-cards';
import { AppIcon, AppImage } from '@impacta/shared';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import type { FAQItem } from '@/data/parseFaqs';
import FaqAccordion from '@/components/FaqAccordion';
import { TIERS, matchTierKey, GiftCardPreview, GlassmorphicGiftCardPreview } from '@impacta/shared';
import GiftCardForm, { type GiftCardFormData } from './GiftCardForm';
import GiftCardTierSelector, { type TierInfo } from './GiftCardTierSelector';
import HowItWorks from './HowItWorks';

/* ─── Static content ─── */

const experienceBenefits = [
  {
    icon: 'HeartIcon',
    title: 'Conexión emocional profunda',
    description:
      'Las experiencias crean recuerdos que duran toda la vida, fortaleciendo vínculos entre personas.',
  },
  {
    icon: 'SparklesIcon',
    title: 'Descubrimiento personal',
    description:
      'Cada actividad es una oportunidad de descubrir talentos ocultos y pasiones que no sabías que tenías.',
  },
  {
    icon: 'UserGroupIcon',
    title: 'Comunidad y pertenencia',
    description:
      'Compartir una experiencia creativa genera lazos auténticos con otras personas que comparten tus intereses.',
  },
  {
    icon: 'GlobeAltIcon',
    title: 'Sostenibilidad real',
    description:
      'Sin embalajes, sin residuos. Regalar experiencias es la opción más consciente con el planeta.',
  },
  {
    icon: 'StarIcon',
    title: 'Valor que no se deprecia',
    description:
      'A diferencia de los objetos, los recuerdos de una experiencia se vuelven más valiosos con el tiempo.',
  },
  {
    icon: 'LightBulbIcon',
    title: 'Creatividad y bienestar',
    description:
      'Las actividades creativas reducen el estrés, estimulan la mente y mejoran el bienestar general.',
  },
];

const testimonials = [
  {
    name: 'Valentina R.',
    role: 'Regalo una actividad de ceramica',
    quote:
      'Mi mejor regalo de cumpleanos! Mi amiga quedo encantada con la actividad de ceramica. Fue una experiencia que recordara siempre. Definitivamente volveria a regalar con IMPACTA.red.',
    avatar: '/assets/images/regalar/testimonial-1.png',
    avatarAlt: 'Valentina, mujer joven sonriendo con cabello oscuro',
    rating: 5,
  },
  {
    name: 'Matias C.',
    role: 'Regalo taller de cocina',
    quote:
      'Buscaba algo diferente para el aniversario y esto fue perfecto. El proceso fue super facil, llego al correo al instante y mi pareja eligio la actividad que mas le gusto. 100% recomendado.',
    avatar: '/assets/images/regalar/testimonial-2.png',
    avatarAlt: 'Matias, hombre joven con barba y sonrisa amigable',
    rating: 5,
  },
  {
    name: 'Camila F.',
    role: 'Regalo retiro de yoga',
    quote:
      'Le regale a mi mama una giftcard para su cumpleanos y eligio un retiro de yoga. Nos dijo que fue lo mejor que le han regalado en anos. Las experiencias valen mas que cualquier objeto.',
    avatar: '/assets/images/regalar/testimonial-3.jpg',
    avatarAlt: 'Camila, mujer joven con cabello rubio y expresion alegre',
    rating: 5,
  },
];

/* ─── Component ─── */

interface RegalarClientProps {
  variants: GiftCardVariant[];
  giftCardFaqs: FAQItem[];
}

export default function RegalarClient({ variants, giftCardFaqs }: RegalarClientProps) {
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewStyle, setPreviewStyle] = useState<'classic' | 'glassmorphic'>('classic');
  const router = useRouter();
  const { addItem, openDrawer } = useCart();
  const formRef = useRef<HTMLDivElement>(null);

  // Scroll to hash on mount / hash change (delay lets layout + images settle)
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash) return;
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (!element) return;

      requestAnimationFrame(() => {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 350);
      });
    };

    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  // Build tier info from real Shopify variants
  const tiers: TierInfo[] = variants.map((v) => {
    const key = matchTierKey(v.title);
    const config = TIERS[key];
    return {
      id: v.id,
      name: config.name,
      price: v.price,
      description: config.description,
      tierKey: key,
    };
  });

  const selectedTier = tiers.find((t) => t.id === selectedTierId) ?? null;

  function handleTierSelect(id: string) {
    setSelectedTierId(id);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  async function handleAddToCart(data: GiftCardFormData) {
    if (!selectedTierId) return;
    setIsSubmitting(true);
    try {
      const attributes = [
        { key: '_is_gift', value: 'true' },
        { key: '_recipient_name', value: data.recipientName },
        { key: '_recipient_email', value: data.recipientEmail },
        { key: '_sender_email', value: data.senderEmail },
        ...(data.message ? [{ key: '_gift_message', value: data.message }] : []),
      ];
      await addItem(selectedTierId, 1, attributes);
      setSelectedTierId(null);
      openDrawer();
      router.back();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-white" id="main-content">
      {/* ─── CLOSE BUTTON ─── */}
      <button
        onClick={() => router.back()}
        aria-label="Cerrar"
        className="fixed top-5 right-5 z-[9999] flex items-center justify-center w-11 h-11 rounded-full bg-white shadow-lg text-[#373A36] hover:bg-[#00A19B] hover:text-white transition-all duration-200 hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2.5}
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* ─── 1. HERO SECTION ─── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <AppImage
            src="/assets/images/regalar/hero.png"
            alt="Manos entregando un regalo envuelto con lazo, gesto de generosidad y celebración"
            fill
            className="object-cover"
            sizes="100vw"
            priority
          />
          {/* Multi-layer overlay for vibrancy */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(135deg, rgba(0,161,155,0.82) 0%, rgba(0,80,77,0.70) 40%, rgba(55,58,54,0.75) 100%)',
            }}
          />
          {/* Decorative color blobs */}
          <div
            className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{ background: '#E0DF00' }}
          />
          <div
            className="absolute bottom-1/4 left-1/4 w-64 h-64 rounded-full opacity-15 blur-3xl"
            style={{ background: '#E0DF00' }}
          />
        </div>

        {/* Brand logo – top left */}
        <div className="absolute top-6 left-6 z-20">
          <AppLogo size={36} className="brightness-0 invert" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center py-32">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-8 text-sm font-bold"
            style={{
              background: 'rgba(224,223,0,0.2)',
              border: '1px solid rgba(224,223,0,0.5)',
              color: '#E0DF00',
            }}
          >
            <AppIcon name="GiftIcon" size={16} />
            Tarjetas regalo IMPACTA.red
          </div>

          <h1 className="text-5xl lg:text-7xl font-extrabold text-white leading-tight mb-6">
            Descubre el{' '}
            <span className="relative inline-block">
              <span style={{ color: '#E0DF00' }}>Regalo</span>
            </span>
            <br />
            Perfecto
          </h1>

          <p className="text-xl lg:text-2xl text-white/85 leading-relaxed mb-10 max-w-2xl mx-auto">
            Regala emociones, no objetos. Una experiencia IMPACTA.red crea recuerdos que duran toda
            la vida y conecta a las personas de manera auténtica.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-14">
            <button
              onClick={() =>
                document.getElementById('giftcards')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="inline-flex items-center justify-center gap-2 px-10 py-5 rounded-full font-bold text-lg transition-all duration-200 hover:scale-105 hover:shadow-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2"
              style={{ background: '#E0DF00', color: '#373A36' }}
            >
              <AppIcon name="GiftIcon" size={20} />
              Explorar Giftcards
            </button>
            <button
              onClick={() =>
                document.getElementById('beneficios')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="inline-flex items-center justify-center gap-2 px-10 py-5 rounded-full font-bold text-lg transition-all duration-200 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2"
              style={{ border: '2px solid rgba(255,255,255,0.7)', color: 'white' }}
            >
              ¿Por qué experiencias?
            </button>
          </div>

          {/* Trust stats */}
          {FEATURE_STATS_ENABLED && (
            <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
              {[
                { value: '+50', label: 'Actividades' },
                { value: '12', label: 'Meses válida' },
                { value: '100%', label: 'Seguro' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-3xl font-extrabold" style={{ color: '#E0DF00' }}>
                    {stat.value}
                  </p>
                  <p className="text-white/70 text-sm font-medium mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scroll indicator */}
        <button
          onClick={() =>
            document.getElementById('giftcards')?.scrollIntoView({ behavior: 'smooth' })
          }
          aria-label="Ir a selección de giftcards"
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00A19B] focus-visible:outline-offset-2 rounded"
        >
          <AppIcon name="ChevronDownIcon" size={28} className="text-white/80" />
        </button>
      </section>

      {/* ─── 2. TIER SELECTION ─── */}
      <section
        id="giftcards"
        className="py-20"
        style={{ background: 'linear-gradient(180deg, #FAFAF8 0%, white 100%)' }}
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <p
              className="text-sm font-bold uppercase tracking-widest mb-3"
              style={{ color: '#00A19B' }}
            >
              Elige tu tarjeta
            </p>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-[#373A36]">
              Selecciona el monto
            </h2>
          </div>

          <GiftCardTierSelector
            tiers={tiers}
            selectedId={selectedTierId}
            onSelect={handleTierSelect}
          />
        </div>
      </section>

      {/* ─── 3. FORM + PREVIEW (shown when tier selected) ─── */}
      {selectedTier && (
        <section ref={formRef} className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Preview (left on desktop) */}
              <div className="space-y-4">
                <GiftCardPreview
                  tierName={selectedTier.name}
                  displayPrice={TIERS[selectedTier.tierKey].displayPrice}
                  tierKey={selectedTier.tierKey}
                />
              </div>

              {/* Form (right on desktop) */}
              <GiftCardForm
                tierName={selectedTier.name}
                displayPrice={TIERS[selectedTier.tierKey].displayPrice}
                onSubmit={handleAddToCart}
                isSubmitting={isSubmitting}
                onSuccess={() => setSelectedTierId(null)}
                key={selectedTierId}
              />
            </div>
          </div>
        </section>
      )}

      {/* ─── 4. BENEFICIOS DE REGALAR EXPERIENCIAS ─── */}
      <section id="beneficios" className="py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p
              className="text-sm font-bold uppercase tracking-widest mb-3"
              style={{ color: '#00A19B' }}
            >
              El valor de lo intangible
            </p>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-[#373A36] mb-5">
              Beneficios de Regalar <span style={{ color: '#00A19B' }}>Experiencias</span>
            </h2>
            <p className="text-lg text-[#6b6e68] max-w-2xl mx-auto leading-relaxed">
              Las experiencias crean conexiones emocionales que los objetos materiales nunca pueden
              lograr. Descubre por qué regalar vivencias es la elección más significativa.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-0">
            {/* Left column */}
            <div className="space-y-0">
              {experienceBenefits.slice(0, 3).map((benefit, index) => (
                <div
                  key={benefit.title}
                  className="flex gap-5 py-7 border-b border-[#F0F0EC] last:border-b-0"
                >
                  <div
                    className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ background: index === 1 ? '#E0DF00' : '#e6f7f6' }}
                  >
                    <AppIcon
                      name={benefit.icon as any}
                      size={24}
                      style={{ color: index === 1 ? '#373A36' : '#00A19B' }}
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#373A36] mb-1.5">{benefit.title}</h3>
                    <p className="text-[#6b6e68] text-sm leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Right column */}
            <div className="space-y-0">
              {experienceBenefits.slice(3).map((benefit, index) => (
                <div
                  key={benefit.title}
                  className="flex gap-5 py-7 border-b border-[#F0F0EC] last:border-b-0"
                >
                  <div
                    className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ background: index === 0 ? '#E0DF00' : '#e6f7f6' }}
                  >
                    <AppIcon
                      name={benefit.icon as any}
                      size={24}
                      style={{ color: index === 0 ? '#373A36' : '#00A19B' }}
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#373A36] mb-1.5">{benefit.title}</h3>
                    <p className="text-[#6b6e68] text-sm leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── 5. HOW IT WORKS ─── */}
      <HowItWorks />

      {/* ─── 6. CTA SECTION ─── */}
      <section className="relative py-28 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #00A19B 0%, #007a75 35%, #005c58 65%, #373A36 100%)',
          }}
        />
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
          style={{ background: '#E0DF00', transform: 'translate(30%, -30%)' }}
        />
        <div
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-15 blur-3xl"
          style={{ background: '#E0DF00', transform: 'translate(-30%, 30%)' }}
        />
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '30px 30px',
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          {/* Emoji burst */}
          <div className="flex justify-center gap-3 text-4xl mb-8">
            <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
              🎁
            </span>
            <span className="animate-bounce" style={{ animationDelay: '150ms' }}>
              ✨
            </span>
            <span className="animate-bounce" style={{ animationDelay: '300ms' }}>
              🎉
            </span>
          </div>

          <h2 className="text-4xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
            Regala una <span style={{ color: '#E0DF00' }}>Experiencia</span> Hoy
          </h2>

          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
            No esperes más para sorprender a esa persona especial. Entrega inmediata, sin
            complicaciones, con el respaldo de IMPACTA.red.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <button
              onClick={() =>
                document.getElementById('giftcards')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="inline-flex items-center justify-center gap-3 px-12 py-5 rounded-full font-bold text-xl transition-all duration-200 hover:scale-105 hover:shadow-2xl"
              style={{ background: '#E0DF00', color: '#373A36' }}
            >
              <AppIcon name="GiftIcon" size={24} />
              Regalar ahora
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {[
              { icon: 'BoltIcon', text: 'Entrega inmediata' },
              { icon: 'ShieldCheckIcon', text: 'Pago 100% seguro' },
              { icon: 'CalendarDaysIcon', text: 'Válida 12 meses' },
              { icon: 'ArrowPathIcon', text: 'Sin restricciones' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2 text-white/80 text-sm">
                <AppIcon name={item.icon as any} size={16} style={{ color: '#E0DF00' }} />
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 7. TESTIMONIOS ─── */}
      {FEATURE_TESTIMONIOS_ENABLED && (
        <section className="py-24 bg-[#FAFAF8]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <p
                className="text-sm font-bold uppercase tracking-widest mb-3"
                style={{ color: '#00A19B' }}
              >
                Historias reales
              </p>
              <h2 className="text-3xl lg:text-4xl font-extrabold text-[#373A36] mb-4">
                Testimonios de Clientes <span style={{ color: '#00A19B' }}>Satisfechos</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.name}
                  className="relative rounded-3xl p-8 flex flex-col"
                  style={{
                    background:
                      index === 1 ? 'linear-gradient(135deg, #00A19B 0%, #007a75 100%)' : 'white',
                    border: index === 1 ? 'none' : '1px solid #E8E8E4',
                    boxShadow:
                      index === 1
                        ? '0 20px 60px rgba(0,161,155,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.06)',
                  }}
                >
                  <div
                    className="text-6xl font-serif leading-none mb-4"
                    style={{ color: index === 1 ? 'rgba(255,255,255,0.3)' : '#E0DF00' }}
                  >
                    &ldquo;
                  </div>

                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <span
                        key={i}
                        className="text-lg"
                        style={{ color: index === 1 ? '#E0DF00' : '#F59E0B' }}
                      >
                        &#9733;
                      </span>
                    ))}
                  </div>

                  <p
                    className="text-base leading-relaxed flex-1 mb-6"
                    style={{ color: index === 1 ? 'rgba(255,255,255,0.92)' : '#4B4E47' }}
                  >
                    {testimonial.quote}
                  </p>

                  <div className="flex items-center gap-4">
                    <div
                      className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0"
                      style={{
                        border: `2px solid ${index === 1 ? 'rgba(255,255,255,0.4)' : '#E0DF00'}`,
                      }}
                    >
                      <AppImage
                        src={testimonial.avatar}
                        alt={testimonial.avatarAlt}
                        fill
                        className="object-cover"
                        sizes="48px"
                      />
                    </div>
                    <div>
                      <p
                        className="font-bold text-sm"
                        style={{ color: index === 1 ? 'white' : '#373A36' }}
                      >
                        {testimonial.name}
                      </p>
                      <p
                        className="text-xs"
                        style={{ color: index === 1 ? 'rgba(255,255,255,0.85)' : '#6b6e68' }}
                      >
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── 8. FAQ SECTION ─── */}
      <FaqAccordion
        faqs={giftCardFaqs}
        accentColor="#00A19B"
        accentLight="#e6f7f6"
        accentBg="#f0fffe"
        accentBorder="rgba(0,161,155,0.2)"
        maxHeight={300}
      />
    </main>
  );
}
